import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ApiService } from '@shared/services/api.service';
import type { FloorPlaceDto, FloorPresenceDto, FloorPresenceUserDto } from 'nexgen-types';
import { useFloorManagerStore } from './FloorManagerStore';
import { Floor } from '../model/types';
import { UserStore } from './userStore';
import { useWorkplaceStore } from '@/host/stores/workplace.store';
import { isCurrentUserGuest } from '@/utils/guestLogoutUtils';

export interface FloorState {
  pid: string;
  place?: FloorPlaceDto;
  presence?: FloorPresenceDto;
}

interface MultiFloorStateStore {
  floors: Record<string, FloorState>;
  floorsSelected: Record<string, boolean>;
  isLoadingFloorsSelected: boolean;
  // True only once loadFloorsSelected() has actually completed successfully.
  // buildingFloors/floorsSelected being non-empty is NOT a safe stand-in for this —
  // buildingFloors is populated by an unrelated, faster office-list fetch.
  floorsSelectedLoaded: boolean;
  // Bumped on every resetState() (org switch). loadFloorsSelected() captures this
  // at call start and re-checks it before applying its result, so a stale call
  // started for the previous org can't overwrite a newer org's state after it
  // resolves late.
  loadGeneration: number;
  floorsPreview: string[];
  currentFloorPresence: string | null;

  setFloorsPreview: (floorsPreview: string[]) => Promise<void>;
  addFloorsToPreview: (floorPids: string[]) => void;
  setCurrentFloorPresence: (floorPid: string | null) => void;

  addFloorState: (pid: string) => void;
  getFloorState: (pid: string) => FloorState | undefined;

  setPlace: (pid: string, place: FloorPlaceDto) => void;
  setPlaceWithoutRooms: (pid: string, place: Omit<FloorPlaceDto, 'rooms'>) => void;
  setPresence: (pid: string, presence: FloorPresenceDto) => void;
  updateUserPresence: (floorPid: string, roomId: string, userPid: string) => void;
  updateUserStatus: (floorPid: string, userPid: string, status: string, message?: string) => void;
  updateGuestPresence: (pid: string, guest: FloorPresenceUserDto) => void;
  updateRoomOooMessage: (floorPid: string, roomPid: string, oooMessage: string) => void;
  clearPresence: (pid: string) => void;
  removeUserFromPresence: (userPid: string) => void;
  removeUserFromFloor: (floorPid: string, userPid: string) => void;
  updateUserName: (userPid: string, name: string) => void;

  fetchPlace: (pid: string) => Promise<void>;
  fetchPresence: (pid: string) => Promise<void>;
  loadInitialState: (pid: string) => Promise<void>;

  buildingFloors: Floor[];
  setBuildingFloors: (floors: Floor[]) => void;
  removeFloor: (pid: string) => void;
  resetState: () => void;

  // New methods for floors selected state
  setFloorSelected: (pid: string, selected: boolean) => void;
  setFloorsSelected: (floorsSelected: Record<string, boolean>) => void;
  loadFloorsSelected: () => Promise<void>;
  saveFloorsSelected: () => Promise<void>;
  isFloorSelected: (pid: string) => boolean;
}

export const useMultiFloorStateStore = create<MultiFloorStateStore>()(
  persist(
    (set, get) => ({
      floors: {},
      buildingFloors: [],
      floorsSelected: {},
      isLoadingFloorsSelected: false,
      floorsSelectedLoaded: false,
      loadGeneration: 0,
      floorsPreview: [],
      currentFloorPresence: null,

      setCurrentFloorPresence: (floorPid: string | null) => {
        set({ currentFloorPresence: floorPid });
      },

      setFloorsPreview: async (floorsPreview: string[]) => {
        const currentFloorPresence = get().currentFloorPresence;

        // Ensure currentFloorPresence is always included
        const finalPreview =
          currentFloorPresence && !floorsPreview.includes(currentFloorPresence)
            ? [currentFloorPresence, ...floorsPreview]
            : floorsPreview;

        const currentPreview = get().floorsPreview;
        const newFloors = finalPreview.filter((pid) => !currentPreview.includes(pid));
        const removedFloors = currentPreview.filter((pid) => !finalPreview.includes(pid));

        // Load place and presence for new floors (CarouselView uses this)
        await Promise.all(
          newFloors.map(async (pid) => {
            try {
              get().addFloorState(pid);
              await get().loadInitialState(pid);
            } catch (error) {
              console.error(`[MultiFloorStore] Failed to load floor ${pid}:`, error);
            }
          })
        );

        // Clear presence for removed floors (keep place data)
        removedFloors.forEach((pid) => {
          get().clearPresence(pid);
        });

        set({ floorsPreview: finalPreview });
      },

      addFloorsToPreview: (floorPids: string[]) => {
        const currentPreview = get().floorsPreview;

        // Add only new floors that are not already in preview
        const newFloors = floorPids.filter((pid) => !currentPreview.includes(pid));

        if (newFloors.length === 0) {
          return; // Nothing to add
        }

        // Add floor states without loading data
        newFloors.forEach((pid) => {
          get().addFloorState(pid);
        });

        // Update floorsPreview with new floors
        const updatedPreview = [...currentPreview, ...newFloors];
        set({ floorsPreview: updatedPreview });
      },

      addFloorState: (pid) => {
        if (!get().floors[pid]) {
          set((state) => ({ floors: { ...state.floors, [pid]: { pid } } }));
        }
      },

      removeFloor: (pid: string) => {
        set((state) => ({
          floors: Object.fromEntries(Object.entries(state.floors).filter(([key]) => key !== pid)),
        }));
      },

      getFloorState: (pid) => get().floors[pid],

      setPlace: (pid, place) => {
        set((state) => ({
          floors: {
            ...state.floors,
            [pid]: { ...state.floors[pid], place },
          },
        }));

        const workplaceStore = useWorkplaceStore.getState();
        const currentFloorPid = workplaceStore.currentRoom.floor_pid;
        const currentRoomPid = workplaceStore.currentRoom.pid;

        if (currentFloorPid === pid && currentRoomPid) {
          const currentRoom = place.rooms.find((room) => room.pid === currentRoomPid);

          if (currentRoom) {
            workplaceStore.setCurrentRoom({
              ...workplaceStore.currentRoom,
              name: currentRoom.name ?? workplaceStore.currentRoom.name,
              background_url:
                currentRoom.background_url ?? workplaceStore.currentRoom.background_url,
              type: currentRoom.type ?? workplaceStore.currentRoom.type,
              displayName: currentRoom.displayName ?? workplaceStore.currentRoom.displayName,
            });
          }
        }
      },

      setPlaceWithoutRooms: (pid: string, place: Omit<FloorPlaceDto, 'rooms'>) => {
        set((state) => {
          const currentFloor = state.floors[pid];
          const updatedPlace: FloorPlaceDto = {
            ...currentFloor?.place,
            ...place,
            rooms: currentFloor?.place?.rooms ?? [],
          };

          return {
            floors: {
              ...state.floors,
              [pid]: {
                ...(currentFloor ?? { pid }),
                place: updatedPlace,
              },
            },
          };
        });
      },

      setBuildingFloors: (floors: Floor[]) => {
        set({ buildingFloors: floors });
      },

      setPresence: (pid, presence) => {
        set((state) => {
          if (!state.floors[pid]) {
            return state;
          }

          const updatedFloors = { ...state.floors };

          const newUserPids = presence.users.map((user) => user.pid);
          const newGuestPids = presence.guests?.map((guest) => guest.pid) || [];

          const updatedFloorsEntries = Object.entries(updatedFloors).map(([floorPid, floor]) => {
            if (floorPid === pid || !floor?.presence) {
              return [floorPid, floor];
            }

            const currentPresence = floor.presence;

            const filteredUsers = currentPresence.users.filter(
              (user) => !newUserPids.includes(user.pid)
            );

            const filteredGuests =
              currentPresence.guests?.filter((guest) => !newGuestPids.includes(guest.pid)) || [];

            return [
              floorPid,
              {
                ...floor,
                presence: {
                  ...currentPresence,
                  users: filteredUsers,
                  guests: filteredGuests,
                },
              },
            ];
          });

          const finalFloors = Object.fromEntries(updatedFloorsEntries);
          finalFloors[pid] = {
            ...finalFloors[pid],
            presence: {
              ...presence,
              users: [...presence.users],
              guests: [...(presence.guests ?? [])],
            },
          };

          return { floors: finalFloors };
        });
      },

      updateUserPresence: (floorPid: string, roomId: string, userPid: string) => {
        const floor = get().floors[floorPid];
        const presence = floor?.presence;
        if (!presence || presence.pid !== floorPid) {
          console.warn(`Cannot update user for floor ${floorPid}: invalid presence state`);
          return;
        }

        const existingUser = presence.users.find((u) => u.pid === userPid);
        if (!existingUser) return;

        const updatedUser = {
          ...existingUser,
          room: roomId,
          updated_at: new Date(),
        };

        const users = [...presence.users.filter((u) => u.pid !== userPid), updatedUser];

        // Only update currentFloorPresence if it's the current user moving
        const workplaceStore = useWorkplaceStore.getState();
        const isCurrentUser = workplaceStore.user?.pid === userPid;

        set((state) => ({
          floors: {
            ...state.floors,
            [floorPid]: { ...floor, presence: { ...presence, users } },
          },
          ...(isCurrentUser && { currentFloorPresence: floorPid }),
        }));
      },

      updateUserStatus: (floorPid: string, userPid: string, status: string, message?: string) => {
        const floor = get().floors[floorPid];
        const presence = floor?.presence;
        if (!presence || presence.pid !== floorPid) {
          console.warn(`Cannot update user status for floor ${floorPid}: invalid presence state`);
          return;
        }

        const existingUser = presence.users.find((u) => u.pid === userPid);
        if (!existingUser) return;

        const updatedUser = {
          ...existingUser,
          status: status as any,
          message: message || '',
          updated_at: new Date(),
        };

        const users = [...presence.users.filter((u) => u.pid !== userPid), updatedUser];

        set((state) => ({
          floors: {
            ...state.floors,
            [floorPid]: { ...floor, presence: { ...presence, users } },
          },
        }));
      },

      updateGuestPresence: (pid, guest) => {
        const floor = get().floors[pid];
        const presence = floor?.presence;
        if (!presence || presence.pid !== pid) {
          console.warn(`Cannot update guest for floor ${pid}: invalid presence state`);
          return;
        }

        const existingGuest = presence.guests?.find((g) => g.pid === guest.pid);
        if (!existingGuest) return;

        const updatedGuest = {
          ...existingGuest,
          ...guest,
          updated_at: new Date(),
        };

        const guests = [
          ...(presence.guests?.filter((g) => g.pid !== guest.pid) || []),
          updatedGuest,
        ];

        set((state) => ({
          floors: {
            ...state.floors,
            [pid]: { ...floor, presence: { ...presence, guests } },
          },
        }));
      },

      updateRoomOooMessage: (floorPid, roomPid, oooMessage) => {
        const floor = get().floors[floorPid];
        const place = floor?.place;
        if (!place || place.pid !== floorPid) {
          console.warn(`Cannot update room OOO message for floor ${floorPid}: invalid place state`);
          return;
        }

        const roomIndex = place.rooms.findIndex((r) => r.pid === roomPid);
        if (roomIndex === -1) {
          console.warn(`Room ${roomPid} not found in floor ${floorPid}`);
          return;
        }

        const updatedRooms = [...place.rooms];
        updatedRooms[roomIndex] = {
          ...updatedRooms[roomIndex],
          ooo_message: oooMessage,
        };

        set((state) => ({
          floors: {
            ...state.floors,
            [floorPid]: {
              ...floor,
              place: {
                ...place,
                rooms: updatedRooms,
              },
            },
          },
        }));
      },

      clearPresence: (pid) => {
        set((state) => ({
          floors: {
            ...state.floors,
            [pid]: { ...state.floors[pid], presence: undefined },
          },
        }));
      },

      // The name shown on a member's avatar, on every floor they appear on — used after a
      // profile save, which the backend does not broadcast as a presence change.
      updateUserName: (userPid, name) => {
        set((state) => {
          const updatedFloors = { ...state.floors };

          Object.keys(updatedFloors).forEach((floorPid) => {
            const floor = updatedFloors[floorPid];
            if (!floor?.presence?.users.some((u) => u.pid === userPid)) return;

            updatedFloors[floorPid] = {
              ...floor,
              presence: {
                ...floor.presence,
                users: floor.presence.users.map((u) => (u.pid === userPid ? { ...u, name } : u)),
              },
            };
          });

          return { floors: updatedFloors };
        });
      },

      // Drops a user/guest from one floor's cached presence only — used for FloorLeave,
      // where the user may already be (or soon be) present on another previewed floor.
      removeUserFromFloor: (floorPid, userPid) => {
        set((state) => {
          const floor = state.floors[floorPid];
          if (!floor?.presence) return state;

          return {
            floors: {
              ...state.floors,
              [floorPid]: {
                ...floor,
                presence: {
                  ...floor.presence,
                  users: floor.presence.users.filter((u) => u.pid !== userPid),
                  guests: floor.presence.guests?.filter((g) => g.pid !== userPid) || [],
                },
              },
            },
          };
        });
      },

      removeUserFromPresence: (userPid) => {
        set((state) => {
          const updatedFloors = { ...state.floors };

          Object.keys(updatedFloors).forEach((floorPid) => {
            const floor = updatedFloors[floorPid];
            if (floor?.presence) {
              const filteredUsers = floor.presence.users.filter((u) => u.pid !== userPid);
              const filteredGuests = floor.presence.guests?.filter((g) => g.pid !== userPid) || [];

              updatedFloors[floorPid] = {
                ...floor,
                presence: {
                  ...floor.presence,
                  users: filteredUsers,
                  guests: filteredGuests,
                },
              };
            }
          });

          return { floors: updatedFloors };
        });
      },

      fetchPlace: async (pid) => {
        try {
          const place = await ApiService.getInstance().get(`/office/floor/${pid}/place`);
          get().setPlace(pid, place);
        } catch (error) {
          console.error('Failed to fetch place:', error);
          throw error;
        }
      },

      fetchPresence: async (pid) => {
        try {
          const presence = await ApiService.getInstance().get(`/office/floor/${pid}/presence`);
          get().setPresence(pid, presence);
        } catch (error) {
          console.error('Failed to fetch presence:', error);
          throw error;
        }
      },

      loadInitialState: async (pid) => {
        try {
          await Promise.all([get().fetchPlace(pid), get().fetchPresence(pid)]);
        } catch (error) {
          console.error('Failed to load initial state:', error);
          throw error;
        }
      },

      setFloorSelected: (pid: string, selected: boolean) => {
        const workplaceStore = useWorkplaceStore.getState();
        const homeFloorPid = workplaceStore.profile?.home_floor_pid;

        set((state) => ({
          floorsSelected: {
            ...state.floorsSelected,
            [pid]: selected,
            [homeFloorPid as string]: true,
          },
        }));
      },

      setFloorsSelected: (floorsSelected: Record<string, boolean>) => {
        set({ floorsSelected });
      },

      loadFloorsSelected: async () => {
        // Captured before any await, so a resetState() (org switch) that happens
        // any time after this point — including mid-flight, while this call is
        // still awaiting a response — bumps loadGeneration and makes every
        // isStale() check below true, so this call's (now stale) result is
        // discarded instead of clobbering the newer org's state. Declared outside
        // the try/catch so the error path can check it too.
        const generation = get().loadGeneration;
        const isStale = () => get().loadGeneration !== generation;

        try {
          if (isCurrentUserGuest()) {
            set({ isLoadingFloorsSelected: false });
            return;
          }

          set({ isLoadingFloorsSelected: true });

          const { profile } = useWorkplaceStore.getState();
          const isAdmin = profile?.roles?.some((role: any) =>
            ['administrator', 'owner'].includes(role.role)
          );

          // Get current persisted state as fallback
          const currentState = get().floorsSelected;
          const hasPersistedState = Object.keys(currentState).length > 0;

          // Fetch user preferences
          const response = await ApiService.getInstance().request({
            method: 'GET',
            endpoint: 'user-org-preferences/app-settings/office',
          });

          let floorsSelected = (response as any)?.settings?.floorsSelected || {};

          // If backend returns empty but we have persisted state, use persisted state temporarily
          // This prevents losing state during hot reloads before backend responds
          if (Object.keys(floorsSelected).length === 0 && hasPersistedState) {
            floorsSelected = currentState;
          }

          let floorSelectedFiltered = Object.keys(floorsSelected).filter(
            (floorId: string) => !!floorsSelected[floorId]
          );

          // Non-admin users: always sync so stale/disallowed floors are removed from preferences
          // Admin users: only sync when preferences are empty (they can see all floors)
          const shouldSync = !isAdmin || floorSelectedFiltered.length === 0;

          if (shouldSync) {
            try {
              // Call backend to sync preferences based on user's floor access
              await ApiService.getInstance().post('office/sync-floor-preferences', {});

              // Reload preferences after sync
              const updatedResponse = await ApiService.getInstance().request({
                method: 'GET',
                endpoint: 'user-org-preferences/app-settings/office',
              });

              floorsSelected = (updatedResponse as any)?.settings?.floorsSelected || {};
              floorSelectedFiltered = Object.keys(floorsSelected).filter(
                (floorId: string) => !!floorsSelected[floorId]
              );

              if (floorSelectedFiltered.length === 0) {
                if (isStale()) return;
                set({ floorsSelected, isLoadingFloorsSelected: false, floorsSelectedLoaded: true });
                UserStore.getState().setPreferences((updatedResponse as any)?.settings);
                return;
              }
            } catch (syncError) {
              console.error('Failed to sync floor preferences:', syncError);
              if (isStale()) return;
              set({ floorsSelected, isLoadingFloorsSelected: false, floorsSelectedLoaded: true });
              UserStore.getState().setPreferences((response as any)?.settings);
              return;
            }
          }

          // Fetch all floor places in a single optimized request
          const bootstrapResponse = await ApiService.getInstance().get(
            `/office/bootstrap/active-floors?floor_pids=${floorSelectedFiltered.join(',')}`
          );

          const { floors } = bootstrapResponse as {
            floors: Array<{ pid: string; place: FloorPlaceDto }>;
          };

          if (isStale()) return;

          // Add floor states and set place data for each floor
          const floorManager = useFloorManagerStore.getState();
          floors.forEach(({ pid, place }) => {
            floorManager.addFloor(pid);
            get().setPlaceWithoutRooms(pid, place);
          });

          // Mark the first floor as "my floor" if needed
          if (floors.length > 0 && !floorManager.getMyFloorPid()) {
            floorManager.markMyFloor(floors[0].pid);
          }

          set({ floorsSelected, isLoadingFloorsSelected: false, floorsSelectedLoaded: true });
          UserStore.getState().setPreferences((response as any)?.settings);
        } catch (error) {
          console.error('Failed to load floors selected state:', error);
          if (isStale()) return;
          set({ isLoadingFloorsSelected: false });
        }
      },

      saveFloorsSelected: async () => {
        try {
          const { floorsSelected } = get();

          // Fetch fresh settings right before merging (rather than trusting the
          // locally cached UserStore.preferences snapshot) so unrelated keys
          // written elsewhere since that snapshot was taken aren't clobbered.
          const response = await ApiService.getInstance().request({
            method: 'GET',
            endpoint: 'user-org-preferences/app-settings/office',
          });
          const currentSettings = (response as any)?.settings ?? {};

          const updatedSettings = {
            ...currentSettings,
            floorsSelected: {
              ...(currentSettings?.floorsSelected ?? {}),
              ...floorsSelected,
            },
          };

          await ApiService.getInstance().request({
            method: 'PUT',
            endpoint: 'user-org-preferences/app-settings/office',
            data: { settings: updatedSettings },
          });

          UserStore.getState().setPreferences(updatedSettings);
        } catch (error) {
          console.error('Failed to save floors selected state:', error);
          throw error;
        }
      },

      isFloorSelected: (pid: string) => {
        return get().floorsSelected[pid] || false;
      },

      resetState: () => {
        // Clear persisted state from localStorage to prevent cross-user contamination
        try {
          localStorage.removeItem('multi-floor-store');
        } catch (error) {
          console.error('[MultiFloorStore] ❌ Error clearing localStorage:', error);
        }

        set((state) => ({
          floors: {},
          buildingFloors: [],
          floorsSelected: {},
          isLoadingFloorsSelected: false,
          floorsSelectedLoaded: false,
          loadGeneration: state.loadGeneration + 1,
          floorsPreview: [],
          currentFloorPresence: null,
        }));
      },
    }),
    {
      name: 'multi-floor-store',
      partialize: (state) => ({
        floorsSelected: state.floorsSelected,
      }),
    }
  )
);

export function useFloorStateByPid<T>(
  pid: string,
  selector: (floor: FloorState | undefined) => T
): T {
  return useMultiFloorStateStore((state) => selector(state.floors[pid]));
}

export function useCurrentFloorState<T>(selector: (floor: FloorState | undefined) => T): T {
  const pid = useFloorManagerStore((s) => s.myFloorPid);
  return useMultiFloorStateStore((s) => selector(pid ? s.floors[pid] : undefined));
}
